/**
 * siteNav.js — Canonical site navigation registry for Japan Toll Receipts.
 *
 * Single source of truth for:
 *   • Mobile hamburger menu (full-page modal in Header.jsx)
 *   • Footer accordion columns (mobile) / grid columns (desktop)
 *   • /sitemap page
 *
 * Why one file: when a new public page is added, it should appear in
 * the mobile menu, footer, AND sitemap automatically — no three-file
 * sync dance. Whenever you add a route in App.jsx, also add it here.
 *
 * Conventions:
 *   • `path` MUST NOT include language prefix — the renderer adds `/ja`
 *     or `/en` based on the visitor's current language. Use absolute
 *     `path` only for language-agnostic routes (`/login`, `/register`).
 *   • `label.en` is the English text; `label.ja` is Japanese.
 *   • `highlight: 'green' | 'purple' | 'amber'` adds a color accent.
 *   • `external: true` opens the link in a new tab.
 *
 * Editorial: no audience-track favouritism. Each track gets its own
 * section. Tracks are listed alphabetically inside the SECTIONS array
 * so no one feels demoted.
 */

export const SITE_NAV_SECTIONS = [
  // ── Get Started ───────────────────────────────────────────────────
  {
    key: 'get-started',
    title: { en: 'Get Started',                 ja: 'はじめに' },
    subtitle: { en: 'For all tracks',           ja: '全プラン共通' },
    color: 'emerald',
    items: [
      { label: { en: 'Free Weekly — Always Free',  ja: '無料週次レポート — 永久無料' }, path: '/free-weekly', highlight: 'green' },
      { label: { en: '1-Year Premium Free Trial', ja: '1年間プレミアム無料体験' }, path: '/free-trial', highlight: 'green' },
      { label: { en: 'How It Works',              ja: '仕組み' },                  path: '/how-it-works' },
      { label: { en: 'Pricing',                   ja: '料金' },                    path: '/pricing' },
      { label: { en: 'Why Japan Toll Receipts',   ja: 'JTRが選ばれる理由' },        path: '/why' },
      { label: { en: 'Create Account',            ja: '新規登録' },                path: '/register' },
      { label: { en: 'Log In',                    ja: 'ログイン' },                path: '/login' },
    ],
  },

  // ── Personal ──────────────────────────────────────────────────────
  {
    key: 'personal',
    title: { en: 'Personal',                    ja: '個人プラン' },
    subtitle: { en: 'For individual drivers',   ja: '個人ドライバー向け' },
    color: 'emerald',
    items: [
      { label: { en: 'Overview',                  ja: '概要' },                     path: '/personal' },
      { label: { en: 'Features',                  ja: '機能' },                     path: '/personal/features' },
      { label: { en: 'Family Vehicle List',       ja: '利用車両リスト' },       path: '/personal/family-vehicle-list' },
      { label: { en: 'Reimbursement Reports',     ja: '経費精算レポート' },         path: '/features/reimbursement-reports' },
      { label: { en: 'Pricing',                   ja: '料金' },                     path: '/personal/pricing' },
      { label: { en: 'Personal Limited Offer',    ja: '個人向け限定オファー' },         path: '/personal/limited-time-offer', highlight: 'purple' },
    ],
  },

  // ── Business ──────────────────────────────────────────────────────
  {
    key: 'business',
    title: { en: 'Business Suite',              ja: '法人スイート' },
    subtitle: { en: 'For fleets & finance',     ja: '車両管理・経理部門向け' },
    color: 'purple',
    items: [
      { label: { en: 'Overview',                          ja: '概要' },                      path: '/business' },
      { label: { en: 'Features',                          ja: '機能' },                      path: '/business/features' },
      { label: { en: 'Organization Management',           ja: '組織管理' },                  path: '/business/organization-management', highlight: 'purple' },
      { label: { en: 'Registered Vehicle Registry',       ja: '登録車両リスト' },            path: '/business/authorized-vehicles' },
      { label: { en: 'Unapproved Vehicle Review',         ja: '未承認車両の確認' },      path: '/features/authorized-vehicle-monitoring' },
      { label: { en: 'Corporate ETC Card Management',     ja: '法人ETCカード管理' },         path: '/business/corporate-etc-card-management' },
      { label: { en: 'Fleet Toll Reporting',              ja: '車両管理通行料レポート' },    path: '/business/etc-fleet-toll-reporting' },
      { label: { en: 'Pricing',                           ja: '料金' },                      path: '/business/pricing' },
      { label: { en: 'Business Suite Hub',                ja: '法人向けサービス' },          path: '/business-suite' },
      { label: { en: 'Apply for Business Suite',          ja: '法人向けサービス申し込み' },          path: '/business-suite/apply', highlight: 'green' },
      { label: { en: 'Business Limited Offer',            ja: '法人限定特典' },          path: '/business/limited-time-offer', highlight: 'purple' },
    ],
  },

  // ── Government & Public-Sector ───────────────────────────────────
  {
    key: 'government',
    title: { en: 'Government & Public-Sector', ja: '官公庁・公共部門' },
    subtitle: { en: 'Official multi-vehicle oversight', ja: '公用車管理' },
    color: 'sky',
    items: [
      { label: { en: 'Overview',                       ja: '概要' },                         path: '/government' },
      { label: { en: 'Features',                       ja: '機能' },                         path: '/government/features' },
      { label: { en: 'Organization Management',        ja: '組織管理' },                     path: '/government/organization-management', highlight: 'blue' },
      { label: { en: 'Registered Vehicle Registry',    ja: '登録車両リスト' },               path: '/government/authorized-vehicles' },
      { label: { en: 'Unapproved Vehicle Review',       ja: '未承認車両の確認' },         path: '/features/authorized-vehicle-monitoring' },
      { label: { en: 'Vehicle Toll Resources',         ja: '公用車ETCリソース' },           path: '/government-vehicle-toll-resources-japan' },
      { label: { en: 'Public-Safety Fleet Records',    ja: '公共安全 公用車記録' },         path: '/public-safety-fleet-etc-records-japan' },
      { label: { en: 'Pricing',                        ja: '料金' },                         path: '/government/pricing' },
      { label: { en: 'Start 1-Year Free Trial',        ja: '1年間無料体験を始める' },        path: '/free-trial', highlight: 'green' },
    ],
  },

  // ── Features ──────────────────────────────────────────────────────
  {
    key: 'features',
    title: { en: 'Features',                    ja: '機能' },
    subtitle: { en: 'Every feature page',       ja: '機能一覧' },
    color: 'violet',
    items: [
      { label: { en: 'All Features',                   ja: 'すべての機能' },              path: '/features' },
      { label: { en: 'Registered Vehicle Monitoring',  ja: '登録車両管理' },      path: '/authorized-vehicle-monitoring' },
      { label: { en: 'Reimbursement Reports',          ja: '経費精算レポート' },          path: '/features/reimbursement-reports' },
    ],
  },

  // ── Learning Center (Guides) ──────────────────────────────────────
  {
    key: 'guides',
    title: { en: 'Learning Center',             ja: 'ヘルプセンター' },
    subtitle: { en: 'In-depth bilingual guides', ja: '日英対応の詳しいガイド' },
    color: 'fuchsia',
    items: [
      { label: { en: 'All Guides',                                ja: 'ガイド一覧' },                    path: '/guides' },
      { label: { en: 'How to Get Toll Receipts in Japan',         ja: '高速道路の領収書の入手方法' },    path: '/guides/how-to-get-toll-receipts-in-japan' },
      { label: { en: 'ETC MEISAI — English Guide',                ja: 'ETC利用明細ガイド' },             path: '/guides/etc-meisai-english-guide' },
      { label: { en: 'Japan ETC Usage Statements',                ja: 'ETC利用照会サービスガイド' },     path: '/guides/japan-etc-usage-statement-guide' },
      { label: { en: 'Business ETC Fleet Reporting',              ja: '法人向けETC複数車両レポート' },       path: '/guides/business-etc-fleet-toll-reporting' },
    ],
  },

  // ── Resources (external + tools) ─────────────────────────────────
  {
    key: 'resources',
    title: { en: 'Official Resources',          ja: '公式情報源' },
    subtitle: { en: 'Trusted external sources', ja: '信頼できる外部サイト' },
    color: 'cyan',
    items: [
      { label: { en: 'See all official resources →', ja: 'すべての公式リソース →' }, path: '/official-etc-resources' },
      { label: { en: 'ETC利用照会サービス（MEISAI） (official)',            ja: 'MEISAI（公式）' },          path: 'https://www.etc-meisai.jp/',         external: true },
      { label: { en: 'ETC Mileage Service',          ja: 'ETCマイレージ' },           path: 'https://www.smile-etc.jp/',          external: true },
      { label: { en: 'NEXCO East',                   ja: 'NEXCO東日本' },              path: 'https://www.e-nexco.co.jp/',         external: true },
      { label: { en: 'NEXCO Central',                ja: 'NEXCO中日本' },              path: 'https://www.c-nexco.co.jp/',         external: true },
      { label: { en: 'NEXCO West',                   ja: 'NEXCO西日本' },              path: 'https://www.w-nexco.co.jp/',         external: true },
      { label: { en: 'Shutoko Expressway',           ja: '首都高速道路' },             path: 'https://www.shutoko.jp/',            external: true },
      { label: { en: 'ETC Setup Shops',              ja: 'ETCセットアップ店' },        path: 'https://www.go-etc.jp/shop_search',  external: true },
      { label: { en: 'Toll & Route Search',          ja: '通行料・経路検索' },         path: 'https://www.driveplaza.com/dp/SearchTop', external: true },
    ],
  },

  // ── Trust & Safety ────────────────────────────────────────────────
  {
    key: 'trust',
    title: { en: 'Trust & Safety',              ja: '信頼と安全への取り組み' },
    subtitle: { en: 'How we protect customers', ja: 'お客様を守る取り組み' },
    color: 'rose',
    items: [
      { label: { en: 'Engineering & Architecture',     ja: 'エンジニアリング構成・システム設計' }, path: '/architecture' },
      { label: { en: 'Phishing Safety Center',         ja: 'フィッシング対策センター' },   path: '/etc-phishing-safety' },
      { label: { en: 'Security',                       ja: 'セキュリティ' },               path: '/security' },
      { label: { en: 'Privacy Policy',                 ja: 'プライバシーポリシー' },       path: '/privacy' },
      { label: { en: 'Terms of Service',               ja: '利用規約' },                   path: '/terms' },
      { label: { en: 'Data Handling',                  ja: 'データの取扱い' },               path: '/data-handling' },
      { label: { en: 'Consent Policy',                 ja: '同意ポリシー' },               path: '/consent-policy' },
      { label: { en: 'Disclaimer',                     ja: '免責事項' },                   path: '/disclaimer' },
      { label: { en: 'Refund Policy',                  ja: '返金ポリシー' },               path: '/legal/refund-policy' },
      { label: { en: 'Commercial Transactions Notice', ja: '特定商取引法に基づく表記' },   path: '/legal/commercial-transactions' },
    ],
  },

  // ── Support ───────────────────────────────────────────────────────
  {
    key: 'support',
    title: { en: 'Support',                     ja: 'サポート' },
    subtitle: { en: 'Get help fast',            ja: 'お問い合わせ・よくある質問' },
    color: 'amber',
    items: [
      { label: { en: 'Help & Contact',         ja: 'ヘルプ・お問い合わせ' }, path: '/help' },
      { label: { en: 'FAQ',                    ja: 'よくある質問' },         path: '/faq' },
      { label: { en: 'Contact Us',             ja: 'お問い合わせ' },         path: '/contact' },
      { label: { en: 'Account Recovery',       ja: 'アカウント復旧' },       path: '/account/recovery' },
      { label: { en: 'Reset Password',         ja: 'パスワード再設定' },     path: '/forgot-password' },
    ],
  },

  // ── Company ───────────────────────────────────────────────────────
  {
    key: 'company',
    title: { en: 'Company',                     ja: '会社情報' },
    subtitle: { en: 'About JTR',                ja: 'JTRについて' },
    color: 'slate',
    items: [
      { label: { en: 'Corporate Profile', ja: '会社概要' }, path: '/corporate-profile' },
      { label: { en: 'Founder\u2019s Message — Why JTR was created', ja: '創業者からのメッセージ — JTRを創った理由' }, path: '/founders-message', highlight: 'purple' },
      { label: { en: 'About',                          ja: '私たちについて' },            path: '/about' },
      { label: { en: 'Our Team',                       ja: 'チーム紹介' },            path: '/our-team' },
      { label: { en: 'Testimonials',                   ja: 'お客様の声' },                path: '/testimonials' },
      { label: { en: 'Market Opportunity',             ja: '市場ニーズ' },                path: '/market' },
      { label: { en: 'Policies & Procedures',          ja: '各種ポリシー' },      path: '/policies' },
      { label: { en: 'Marketing',                       ja: 'マーケティング' },            path: '/ad-creatives/', external: true },
    ],
  },

  // ── Site Updates ──────────────────────────────────────────────────
  {
    key: 'updates',
    title: { en: 'Site Updates',                ja: 'サイト更新情報' },
    subtitle: { en: 'Changelog · Roadmap · Live Reporting', ja: '更新履歴・ロードマップ・ライブレポート' },
    color: 'indigo',
    items: [
      { label: { en: 'Changelog',                      ja: '更新履歴' },                  path: '/changelog' },
      { label: { en: 'Roadmap',                        ja: 'ロードマップ' },              path: '/roadmap' },
      { label: { en: 'Live Reporting',                 ja: 'ライブレポート' },            path: '/live-reporting' },
      { label: { en: 'Sitemap',                        ja: 'サイトマップ' },              path: '/sitemap' },
    ],
  },
];

/**
 * Resolve a SITE_NAV_SECTIONS item's `path` into a full URL respecting
 * the current language prefix. Absolute system routes (`/login`,
 * `/register`, `/dashboard`, `/admin`, `/sitemap`) and external URLs
 * (`http*`) are returned as-is.
 */
export function resolveNavPath(path, lang) {
  if (!path) return '#';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/login') || path.startsWith('/register')
      || path.startsWith('/forgot-password') || path.startsWith('/dashboard')
      || path.startsWith('/admin') || path.startsWith('/sitemap')
      || path.startsWith('/account/')) {
    return path;
  }
  // Language-prefixed routes
  return `/${lang}${path}`;
}

/** Tailwind color-family → text/accent classes used in nav rendering. */
export const SECTION_COLOR_CLASSES = {
  emerald:   { dot: 'bg-emerald-500',  text: 'text-emerald-700',  bg: 'bg-emerald-50',  border: 'border-emerald-200',  hover: 'hover:bg-emerald-50' },
  purple:    { dot: 'bg-[#9200C7]',    text: 'text-[#5b0080]',    bg: 'bg-purple-50',   border: 'border-purple-200',   hover: 'hover:bg-purple-50' },
  violet:    { dot: 'bg-violet-500',   text: 'text-violet-700',   bg: 'bg-violet-50',   border: 'border-violet-200',   hover: 'hover:bg-violet-50' },
  sky:       { dot: 'bg-sky-500',      text: 'text-sky-700',      bg: 'bg-sky-50',      border: 'border-sky-200',      hover: 'hover:bg-sky-50' },
  fuchsia:   { dot: 'bg-fuchsia-500',  text: 'text-fuchsia-700',  bg: 'bg-fuchsia-50',  border: 'border-fuchsia-200',  hover: 'hover:bg-fuchsia-50' },
  cyan:      { dot: 'bg-cyan-500',     text: 'text-cyan-700',     bg: 'bg-cyan-50',     border: 'border-cyan-200',     hover: 'hover:bg-cyan-50' },
  rose:      { dot: 'bg-rose-500',     text: 'text-rose-700',     bg: 'bg-rose-50',     border: 'border-rose-200',     hover: 'hover:bg-rose-50' },
  amber:     { dot: 'bg-amber-500',    text: 'text-amber-700',    bg: 'bg-amber-50',    border: 'border-amber-200',    hover: 'hover:bg-amber-50' },
  slate:     { dot: 'bg-slate-500',    text: 'text-slate-700',    bg: 'bg-slate-50',    border: 'border-slate-200',    hover: 'hover:bg-slate-50' },
  indigo:    { dot: 'bg-indigo-500',   text: 'text-indigo-700',   bg: 'bg-indigo-50',   border: 'border-indigo-200',   hover: 'hover:bg-indigo-50' },
};
